import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Circles } from 'react-loading-icons';

const VerifiedPurchaseHandler = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const server_url = process.env.REACT_APP_API_URI;

  useEffect(() => {
    const verifyPurchase = async () => {
      try {
        const response = await axios.get(
          `${server_url}/qr/verify-purchase/${token}`,
        );

        // Store verification details in localStorage so it can be picked up by the Review pages
        // The idea is we grant the user a "verified token" for this specific session
        const verifyData = response.data;
        localStorage.setItem('verifiedPurchase', JSON.stringify(verifyData));

        // After successfully caching the verified data, route them to the specific rating page
        // Wait, normally they rate the business or a specific product.
        // We will route them to `/company-rating/${verifyData.businessId}` and the review page
        // will pick up the `verifiedPurchase` item to lock fields and grant the badge.

        let ratingUrl = `/company-rating/${verifyData.businessId}`;
        if (verifyData.productId) {
          ratingUrl += `?product=${verifyData.productId}`;
        }
        navigate(ratingUrl);
      } catch (err) {
        console.error('Error verifying token:', err);
        setError(err.response?.data?.message || 'Invalid or expired QR Code.');
        setLoading(false);
      }
    };

    verifyPurchase();
  }, [token, navigate, server_url]);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-[#0E1834]">
        <Circles className="text-white mb-4" />
        <h2 className="text-white text-2xl">Verifying your purchase...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-200px)] px-4">
        <div className="bg-gray-800/80 backdrop-blur-sm border border-gray-700 p-8 md:p-12 rounded-3xl max-w-lg w-full text-center shadow-2xl">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-white text-3xl font-bold mb-4">
            Oops! Verification Failed
          </h2>
          <p className="text-gray-300 text-lg mb-4">
            {error === 'This purchase proof has already been used'
              ? 'It looks like a review has already been submitted using this purchase code!'
              : "We couldn't verify this QR code. It might be invalid or expired."}
          </p>
          <p className="text-gray-400 text-sm mb-8">
            Don't worry, you can still leave a regular review for this business
            or discover other amazing places on KnowQuest!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto px-6 py-3 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white rounded-xl font-semibold transition-all duration-300"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-[#F2994A] to-[#F2C94C] text-[#0E1834] rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
            >
              Discover KnowQuest
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VerifiedPurchaseHandler;
